// js/formValidationHandler.js

/**
 * Class handling form validation with Parsley.js,
 * including integration with tabs (Tabs).
 */
export class FormValidationHandler { // Dodano export
    /**
     * @param {string} currentLanguage - Current language for Parsley localization.
     * @param {function} setValidationStateFunction - Reference to the setValidationState function.
     * @param {function} debugLogFunction - Reference to the logging function.
     */
    constructor(currentLanguage, setValidationStateFunction, debugLogFunction) {
        this.currentLanguage = currentLanguage;
        this.setValidationState = setValidationStateFunction; // Store the reference
        this.DCMSDebugLog = debugLogFunction; // Store the reference
        
        if (!window.ParsleyValidator) {
            this.DCMSDebugLog('ParsleyValidator not found. FormValidationHandler will not initialize.', 2, 'FormValidationHandler');
            return;
        }
        
        this.configureParsley();
        this.registerParsleyListeners();
        this.DCMSDebugLog('Parsley listeners registered.', 3, 'FormValidationHandler');
    }

    configureParsley() {
        Object.assign(window.ParsleyConfig, {
            errorsWrapper: '<div style="display:none"></div>',
            errorTemplate: ''
        });
        window.Parsley.setLocale(this.currentLanguage);
        this.DCMSDebugLog(`Parsley configured. Locale set to: ${this.currentLanguage}`, 3, 'FormValidationHandler');
    }

    registerParsleyListeners() {
        window.Parsley.on('field:validated', (field) => {
            const $element = field.$element;
            let id = $element.attr('id');
            const forId = $element.attr('data-id');

            if (forId !== undefined) {
                id = forId;
            }

            if (id === undefined) {
                this.DCMSDebugLog('Field ID is undefined during validation.', 2, 'FormValidationHandler', field);
                return;
            }

            if (field.isValid()) {
                if (typeof this.setValidationState === 'function') {
                    this.setValidationState(id, 1, ''); // 1 = Success
                }
            } else {
                const errors = field.getErrorsMessages();
                if (typeof this.setValidationState === 'function') {
                    this.setValidationState(id, 2, errors.join(', ')); // 2 = Error
                }
            }
        });

        window.Parsley.on('parsley:field:error', (fieldInstance) => {
            // this.DCMSDebugLog('Parsley field error:', 3, 'FormValidationHandler', fieldInstance);
            fieldInstance.options['errorTemplate'] = '<li class="alert alert-danger marginT"></li>';

            if (!fieldInstance._xhr) {
                return;
            }
            if (fieldInstance._xhr.status < 400 || fieldInstance._xhr.status > 499) {
                return;
            }
            fieldInstance.options['remoteMessage'] = fieldInstance._xhr.responseText;
        });

        window.Parsley.on('form:validated', (parsleyForm) => {
            // Collapsible panels: the marker follows the errors left after the validation
            if (typeof window.syncPanelErrorStates === 'function') {
                window.syncPanelErrorStates(parsleyForm.element || document);
            }
            document.querySelectorAll('a.dcms-tab-has-error').forEach((tabLabel) => {
                const panelId = tabLabel.id.replace('-nav', '');
                const tabPane = document.getElementById(panelId);

                if (tabPane) {
                    const errorsOnThisTab = tabPane.querySelectorAll('.parsley-error, .validationError').length;
                    if (errorsOnThisTab === 0) {
                        if (tabLabel.classList.contains('dcms-tab-has-error')) {
                            tabLabel.classList.remove('dcms-tab-has-error');
                            this.DCMSDebugLog(`Global clean: Removed 'dcms-tab-has-error' from TabLabel ID='${tabLabel.id}' as no errors remain on its pane.`, 3, 'FormValidationHandler.form:validated');
                        }
                    }
                } else {
                    if (tabLabel.classList.contains('dcms-tab-has-error')) {
                        tabLabel.classList.remove('dcms-tab-has-error');
                        this.DCMSDebugLog(`Global clean: Removed 'dcms-tab-has-error' from TabLabel ID='${tabLabel.id}' as its pane was not found.`, 2, 'FormValidationHandler.form:validated');
                    }
                }
            });

            if (!parsleyForm.isValid()) {
                // The tab in view wins: when an error is on the active tab (or outside the tabs),
                // stay and focus the first such field. Switch only when every error is on an
                // inactive tab — then to the tab of the first one. Formerly the first error on an
                // inactive tab won even when the active tab showed an error, so the user was taken
                // away from the error in front of them.
                let firstVisibleErrorElement = null;
                let firstHiddenErrorElement = null;

                for (const fieldInstance of parsleyForm.fields) {
                    if (fieldInstance.isValid()) {
                        continue;
                    }
                    const fieldElement = fieldInstance.element;
                    const tabPane = fieldElement.closest('.tab-pane');
                    if (!tabPane || tabPane.classList.contains('active')) {
                        firstVisibleErrorElement = fieldElement;
                        break;
                    }
                    if (!firstHiddenErrorElement) {
                        firstHiddenErrorElement = fieldElement;
                    }
                }

                if (firstVisibleErrorElement) {
                    if (document.activeElement !== firstVisibleErrorElement) {
                        firstVisibleErrorElement.focus();
                    }
                    this.DCMSDebugLog(`Error in view, tab kept: FieldID='${firstVisibleErrorElement.id}'`, 3, 'FormValidationHandler.form:validated');
                } else if (firstHiddenErrorElement) {
                    const firstErrorTabPaneId = firstHiddenErrorElement.closest('.tab-pane').id;
                    const tabLabelToActivate = document.getElementById(firstErrorTabPaneId + '-nav');
                    if (tabLabelToActivate) {
                        this.DCMSDebugLog(`Activating tab with error: LabelID='${tabLabelToActivate.id}'`, 3, 'FormValidationHandler.form:validated');
                        tabLabelToActivate.click();

                        setTimeout(() => {
                            if (document.activeElement !== firstHiddenErrorElement) {
                                firstHiddenErrorElement.focus();
                                this.DCMSDebugLog(`Focused on first error field after tab switch: FieldID='${firstHiddenErrorElement.id}'`, 3, 'FormValidationHandler.form:validated');
                            }
                        }, 100);
                    }
                }
            }
        });
    }
}

// Initialization will now be in main.js